/**
 * Map Error Alert Component - Displays API errors with fallback options
 */
import { useTranslation } from "react-i18next";
import "./MapErrorAlert.css";

export interface MapErrorAlertProps {
    error: Error | null;
    onRetry?: () => void;
    onDismiss?: () => void;
    showFallback?: boolean;
    onUseCached?: () => void;
}

export const MapErrorAlert = ({
    error,
    onRetry,
    onDismiss,
    showFallback = false,
    onUseCached
}: MapErrorAlertProps): JSX.Element | null => {
    const { t } = useTranslation();

    if (!error) {
        return null;
    }

    const isNetworkError =
        error.message.includes("Network") ||
        error.message.includes("Failed to fetch") ||
        error.message.includes("timeout");

    return (
        <div
            className="map-error-alert"
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
        >
            <div className="map-error-alert__content">
                <div className="map-error-alert__icon" aria-hidden="true">
                    ⚠️
                </div>
                <div className="map-error-alert__message">
                    <strong>{t("map.errors.title")}</strong>
                    <p>
                        {isNetworkError
                            ? t("map.errors.network")
                            : t("map.errors.generic")}
                    </p>
                    {showFallback && (
                        <p className="map-error-alert__fallback">
                            {t("map.errors.fallbackAvailable")}
                        </p>
                    )}
                </div>
            </div>

            <div className="map-error-alert__actions">
                {showFallback && onUseCached && (
                    <button
                        type="button"
                        className="map-error-alert__button map-error-alert__button--secondary"
                        onClick={onUseCached}
                    >
                        {t("map.errors.useCached")}
                    </button>
                )}
                {onRetry && (
                    <button
                        type="button"
                        className="map-error-alert__button map-error-alert__button--primary"
                        onClick={onRetry}
                    >
                        {t("map.errors.retry")}
                    </button>
                )}
                {onDismiss && (
                    <button
                        type="button"
                        className="map-error-alert__button map-error-alert__button--text"
                        onClick={onDismiss}
                        aria-label={t("map.errors.dismiss")}
                    >
                        ✕
                    </button>
                )}
            </div>
        </div>
    );
};